import { useCallback, useEffect, useRef } from "react";
import type * as mapboxgl from "mapbox-gl";
import type { ExpressionSpecification } from "mapbox-gl";
import type { Feature, FeatureCollection, Geometry } from "geojson";

import {
  type BoundaryStyle,
  DEFAULT_FOLDER_BOUNDARY_STYLE,
  DEFAULT_PROPERTY_BOUNDARY_STYLE,
  resolveBoundaryStyle,
} from "../../types/boundaryStyle";
import type { Project } from "../../types/project";
import { isGlobeSpanning } from "./homeRegion";
import { type Bbox, bboxPolygon } from "./projectBbox";

const HIGHLIGHT_SOURCE = "listing-highlight";
const HIGHLIGHT_FILL = "listing-highlight-fill";
const HIGHLIGHT_LINE = "listing-highlight-line";
const FOLDERS_SOURCE = "listing-folders";
const FOLDERS_FILL = "listing-folders-fill";
const FOLDERS_LINE = "listing-folders-line";
const FOLDERS_LABEL = "listing-folders-label";
const FOLDERS_BOUNDARY_LINE = "listing-folders-boundary-line";
const FOLDERS_OPEN_LINE = "listing-folders-open-line";
const PROPERTIES_SOURCE = "listing-properties";
const PROPERTIES_LINE = "listing-properties-line";
const FOLDER_ICON = "listing-folder-icon";
/** Amber, the table's folder-icon colour: nothing on the map is amber but folders. */
const FOLDER_COLOUR = "#f59e0b";

const EMPTY: FeatureCollection = { type: "FeatureCollection", features: [] };

/** The "show on map" fly-to is a glance, not a journey. */
const FLY_MS = 400;
/**
 * Closest the camera goes on its own; people can zoom further by hand. High
 * enough that a folder of a few small projects is framed tightly rather
 * than left at a regional view (it was 8; owner asked for tighter fits).
 */
const MAX_AUTO_ZOOM = 13;

export interface FlyToRequest {
  /** What to frame: a project's extent, or a folder's. */
  bounds: Bbox;
  /** Repeat clicks on the same thing fly again. */
  seq: number;
}

/** A sub-folder of the open folder, as the map draws it. */
export interface FolderOverlay {
  id: number;
  name: string;
  /** Union of its projects' extents (recursive); null when none has a location. */
  bbox: Bbox | null;
  projectCount: number;
  /**
   * Its boundary layer, once loaded: drawn as a solid outline in addition to
   * the dashed box around its projects, as a project's property boundaries
   * are drawn with its area.
   */
  boundary?: Geometry | null;
  /** Extent of that boundary, for the label. */
  boundaryBbox?: Bbox | null;
  /** How the boundary is drawn; null = the default amber. */
  boundaryStyle?: BoundaryStyle | null;
}

/** The open folder's own boundary layer, drawn around everything in view. */
export interface OpenFolderBoundary {
  id: number;
  name: string;
  geometry: Geometry;
  style?: BoundaryStyle | null;
}

/** The per-feature paint inputs a boundary carries (see the layers' expressions). */
function styleProps(style: BoundaryStyle) {
  return { color: style.color, width: style.width, opacity: style.opacity };
}
const FOLDER_STYLE = DEFAULT_FOLDER_BOUNDARY_STYLE;
const PROPERTY_STYLE = DEFAULT_PROPERTY_BOUNDARY_STYLE;
const STYLE_COLOUR = (fallback: string): ExpressionSpecification => ["coalesce", ["get", "color"], fallback];
const STYLE_WIDTH = (fallback: number): ExpressionSpecification => ["coalesce", ["get", "width"], fallback];
const STYLE_OPACITY = (fallback: number): ExpressionSpecification => ["coalesce", ["get", "opacity"], fallback];

const HOVERED = ["boolean", ["get", "hovered"], false];
/** What a folder feature is: "box" (dashed extent), "boundary" (a sub-folder's
 *  own layer), "open" (the open folder's layer) or "label". */
const KIND = ["get", "kind"];

function roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

/** A folder glyph drawn at 2x for the label layer; emoji don't render in Mapbox text. */
function folderIconImage(): ImageData | null {
  if (typeof document === "undefined") return null;
  const canvas = document.createElement("canvas");
  canvas.width = 40;
  canvas.height = 32;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.fillStyle = "#fbbf24";
  ctx.strokeStyle = "#78350f";
  ctx.lineWidth = 3;
  roundedRect(ctx, 3, 3, 17, 11, 3); // tab
  ctx.fill();
  ctx.stroke();
  roundedRect(ctx, 3, 9, 34, 20, 4); // body
  ctx.fill();
  ctx.stroke();
  return ctx.getImageData(0, 0, canvas.width, canvas.height);
}

/** The first project layer, so folder shapes can be kept beneath the projects. */
function firstProjectLayerId(map: mapboxgl.Map): string | undefined {
  return map.getStyle()?.layers?.find((l) => l.id.startsWith("project-"))?.id;
}

/**
 * Add the overlay sources and layers once the style can take them. They live
 * outside Map.tsx's `layers` array on purpose: nothing there re-styles,
 * re-queries or fits to them, and a project whose own layers get torn down
 * and re-added needs no re-highlighting.
 */
function ensureOverlays(map: mapboxgl.Map): boolean {
  if (!map.isStyleLoaded()) return false;
  try {
    if (!map.getSource(HIGHLIGHT_SOURCE)) {
      map.addSource(HIGHLIGHT_SOURCE, { type: "geojson", data: EMPTY });
    }
    if (!map.getLayer(HIGHLIGHT_FILL)) {
      map.addLayer({
        id: HIGHLIGHT_FILL,
        type: "fill",
        source: HIGHLIGHT_SOURCE,
        paint: { "fill-color": "#00d4ad", "fill-opacity": 0.35 },
      });
    }
    if (!map.getLayer(HIGHLIGHT_LINE)) {
      map.addLayer({
        id: HIGHLIGHT_LINE,
        type: "line",
        source: HIGHLIGHT_SOURCE,
        layout: { "line-join": "round", "line-cap": "round" },
        paint: { "line-color": "#ffffff", "line-width": 4, "line-opacity": 0.95 },
      });
    }
    if (!map.getSource(FOLDERS_SOURCE)) {
      map.addSource(FOLDERS_SOURCE, { type: "geojson", data: EMPTY });
    }
    const beneath = firstProjectLayerId(map);
    // One fill for boxes and boundaries alike: it is what the hover and click
    // lookups query, so a folder reads the same whichever shape it has. The
    // open folder's own boundary is left out — hovering the ground you are
    // standing on should not light up a row.
    if (!map.getLayer(FOLDERS_FILL)) {
      map.addLayer(
        {
          id: FOLDERS_FILL,
          type: "fill",
          source: FOLDERS_SOURCE,
          filter: ["all", ["==", ["geometry-type"], "Polygon"], ["!=", KIND, "open"]],
          paint: {
            // A restyled boundary fills faintly in its own colour; boxes stay amber.
            "fill-color": STYLE_COLOUR(FOLDER_COLOUR),
            "fill-opacity": ["case", HOVERED, 0.14, ["==", KIND, "boundary"], 0.06, 0.05],
          },
        },
        beneath
      );
    }
    // Dashed: the box around a folder's projects.
    if (!map.getLayer(FOLDERS_LINE)) {
      map.addLayer(
        {
          id: FOLDERS_LINE,
          type: "line",
          source: FOLDERS_SOURCE,
          filter: ["==", KIND, "box"],
          paint: {
            "line-color": FOLDER_COLOUR,
            "line-width": ["case", HOVERED, 2.5, 1.5],
            "line-dasharray": [3, 2],
            "line-opacity": ["case", HOVERED, 1, 0.65],
          },
        },
        beneath
      );
    }
    // Solid: a sub-folder's boundary layer, drawn as well as its box.
    if (!map.getLayer(FOLDERS_BOUNDARY_LINE)) {
      map.addLayer(
        {
          id: FOLDERS_BOUNDARY_LINE,
          type: "line",
          source: FOLDERS_SOURCE,
          filter: ["==", KIND, "boundary"],
          layout: { "line-join": "round" },
          paint: {
            "line-color": STYLE_COLOUR(FOLDER_STYLE.color),
            "line-width": ["case", HOVERED, ["+", STYLE_WIDTH(FOLDER_STYLE.width), 1], STYLE_WIDTH(FOLDER_STYLE.width)],
            "line-opacity": ["case", HOVERED, 1, STYLE_OPACITY(FOLDER_STYLE.opacity)],
          },
        },
        beneath
      );
    }
    // Heavier still: the open folder's own boundary, framing everything in view.
    if (!map.getLayer(FOLDERS_OPEN_LINE)) {
      map.addLayer(
        {
          id: FOLDERS_OPEN_LINE,
          type: "line",
          source: FOLDERS_SOURCE,
          filter: ["==", KIND, "open"],
          layout: { "line-join": "round" },
          paint: {
            // One pixel heavier than the folder's own setting: it frames the view.
            "line-color": STYLE_COLOUR(FOLDER_STYLE.color),
            "line-width": ["+", STYLE_WIDTH(FOLDER_STYLE.width), 1],
            "line-opacity": STYLE_OPACITY(FOLDER_STYLE.opacity),
          },
        },
        beneath
      );
    }
    // Property boundaries of the drawn projects: thin outlines that only show
    // once the camera is close enough for a parcel to be more than a speck,
    // above the project fills and below the highlight and folder labels.
    if (!map.getSource(PROPERTIES_SOURCE)) {
      map.addSource(PROPERTIES_SOURCE, { type: "geojson", data: EMPTY });
    }
    if (!map.getLayer(PROPERTIES_LINE)) {
      map.addLayer({
        id: PROPERTIES_LINE,
        type: "line",
        source: PROPERTIES_SOURCE,
        minzoom: 8,
        layout: { "line-join": "round" },
        paint: {
          // Each project's own style (see propertyStyles); thinner and fainter
          // while zoomed out, the set width and opacity from zoom 11 in.
          "line-color": STYLE_COLOUR(PROPERTY_STYLE.color),
          "line-width": [
            "interpolate",
            ["linear"],
            ["zoom"],
            8,
            ["*", STYLE_WIDTH(PROPERTY_STYLE.width), 0.5],
            11,
            STYLE_WIDTH(PROPERTY_STYLE.width),
          ],
          "line-opacity": [
            "interpolate",
            ["linear"],
            ["zoom"],
            8,
            ["*", STYLE_OPACITY(PROPERTY_STYLE.opacity), 0.4],
            11,
            STYLE_OPACITY(PROPERTY_STYLE.opacity),
          ],
        },
      });
    }
    if (!map.hasImage(FOLDER_ICON)) {
      const image = folderIconImage();
      if (image) map.addImage(FOLDER_ICON, image, { pixelRatio: 2 });
    }
    if (!map.getLayer(FOLDERS_LABEL)) {
      map.addLayer({
        id: FOLDERS_LABEL,
        type: "symbol",
        source: FOLDERS_SOURCE,
        filter: ["==", ["geometry-type"], "Point"],
        layout: {
          "icon-image": FOLDER_ICON,
          "icon-size": 0.9,
          "icon-anchor": "top-left",
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "text-field": [
            "format",
            ["get", "name"],
            {},
            "\n",
            {},
            ["get", "sublabel"],
            { "font-scale": 0.78 },
          ],
          "text-font": ["DIN Pro Bold", "Arial Unicode MS Bold"],
          "text-size": ["interpolate", ["linear"], ["zoom"], 4, 13, 8, 15, 12, 17, 16, 19],
          "text-anchor": "top-left",
          "text-offset": [1.7, -0.1],
          "text-justify": "left",
          "text-line-height": 1.15,
          "text-allow-overlap": true,
          "text-ignore-placement": true,
        },
        paint: {
          "text-color": "#fde68a",
          "text-halo-color": "rgba(15, 23, 42, 0.95)",
          "text-halo-width": 2,
          "text-halo-blur": 0.5,
          "text-opacity": ["case", HOVERED, 1, 0.9],
        },
      });
    }
    return true;
  } catch {
    return false;
  }
}

/** Later-added project layers would otherwise paint over these. */
function raise(map: mapboxgl.Map, ids: string[]) {
  ids.forEach((id) => {
    if (map.getLayer(id)) map.moveLayer(id);
  });
}

/** Keep the faint folder shapes under the projects, whatever order layers arrived in. */
function lowerBelowProjects(map: mapboxgl.Map, ids: string[]) {
  const beneath = firstProjectLayerId(map);
  if (!beneath) return;
  ids.forEach((id) => {
    if (map.getLayer(id)) map.moveLayer(id, beneath);
  });
}

function setData(map: mapboxgl.Map, sourceId: string, data: FeatureCollection) {
  const source = map.getSource(sourceId) as mapboxgl.GeoJSONSource | undefined;
  if (source && typeof source.setData === "function") source.setData(data);
}

/**
 * Each sub-folder as its shapes plus a label point tucked inside its top-left
 * corner: a box padded out from its projects (so the dashed edge visibly
 * encloses them) and, when it has one, its boundary layer as well. The label
 * sits on the box, or on the boundary for a folder with no located projects.
 * Then the open folder's own boundary, unlabelled — the breadcrumb already
 * says where you are.
 */
function folderCollection(
  folders: FolderOverlay[],
  hoveredId: number | null,
  open: OpenFolderBoundary | null
): FeatureCollection {
  const features: Feature[] = [];
  folders.forEach((folder) => {
    const hovered = folder.id === hoveredId;
    const sublabel = `Folder · ${folder.projectCount} project${folder.projectCount === 1 ? "" : "s"}`;
    const label = (anchor: Bbox, padX: number, padY: number) =>
      features.push({
        type: "Feature",
        properties: { id: folder.id, hovered, kind: "label", name: folder.name, sublabel },
        geometry: { type: "Point", coordinates: [anchor[0] + padX * 0.35, anchor[3] - padY * 0.35] },
      });
    if (folder.boundary) {
      features.push({
        type: "Feature",
        properties: {
          id: folder.id,
          hovered,
          kind: "boundary",
          ...styleProps(resolveBoundaryStyle(folder.boundaryStyle, FOLDER_STYLE)),
        },
        geometry: folder.boundary,
      });
    }
    if (folder.bbox) {
      const [w, s, e, n] = folder.bbox;
      const padX = Math.max((e - w) * 0.08, 0.004);
      const padY = Math.max((n - s) * 0.08, 0.004);
      const padded: Bbox = [w - padX, s - padY, e + padX, n + padY];
      features.push({ ...bboxPolygon(padded), properties: { id: folder.id, hovered, kind: "box" } });
      label(padded, padX, padY);
    } else if (folder.boundary && folder.boundaryBbox) {
      const [w, s, e, n] = folder.boundaryBbox;
      label(folder.boundaryBbox, Math.max((e - w) * 0.08, 0.004), Math.max((n - s) * 0.08, 0.004));
    }
  });
  if (open) {
    features.push({
      type: "Feature",
      properties: { id: open.id, hovered: false, kind: "open", ...styleProps(resolveBoundaryStyle(open.style, FOLDER_STYLE)) },
      geometry: open.geometry,
    });
  }
  return { type: "FeatureCollection", features };
}

export interface ListingMapAnnotationOptions {
  map: mapboxgl.Map | null;
  /** The projects currently drawn (the open folder's, or the search results). */
  projects: Project[];
  /**
   * Projects the hover/fly-to lookups may name, including ones hidden from
   * the map: hovering a hidden project's row still outlines where it is.
   * Defaults to `projects`.
   */
  lookupProjects?: Project[];
  /** The map's current extent, after each move (debounced). */
  onViewportChange?: (bounds: Bbox) => void;
  /** Hover from the table (instant); drawn as the highlight overlay. */
  hoveredProjectId: string | null;
  /** A "show on map" click; `seq` makes repeat clicks on one project fly again. */
  flyTo: FlyToRequest | null;
  /** Sub-folders of the open folder, drawn as boxes or boundaries; empty in search mode. */
  folders: FolderOverlay[];
  /** The folder whose row is hovered (instant); its box is emphasised. */
  hoveredFolderId: number | null;
  /** The open folder's own boundary layer, once it has one and it has loaded. */
  openFolderBoundary?: OpenFolderBoundary | null;
  /** Property boundaries of the drawn projects (null while hidden or loading). */
  properties?: FeatureCollection | null;
  /** Each project's property style, by project id (string); missing = default. */
  propertyStyles?: ReadonlyMap<string, BoundaryStyle>;
  /** Changes when the visible set changes (folder, search); never on hover. */
  fitKey?: string;
  /**
   * What to frame when fitKey changes: the open folder's full extent
   * (everything nested beneath it, not only the projects drawn), or the
   * search results. Null when there is nothing with a location.
   */
  fitBounds?: Bbox | null;
  /** Shown when there is nothing to fit, or when the projects span continents. */
  homeBounds?: Bbox | null;
  /** Raw hover from the map; the page applies the dwell. */
  onHoverProject?: (id: string | null) => void;
  onHoverFolder?: (id: number | null) => void;
  /** A click on a folder box that is not also on a project. */
  onOpenFolder?: (id: number) => void;
  /** False while a click-to-place mode owns the pointer. */
  interactive: boolean;
}

/**
 * The listing map's own annotations: project highlight, folder boxes, camera
 * fit on folder/search change, and reporting what the pointer is over.
 * Everything is imperative against the map instance so none of it goes
 * through Map.tsx's layer pipeline.
 */
export function useListingMapAnnotations({
  map,
  projects,
  lookupProjects,
  onViewportChange,
  hoveredProjectId,
  flyTo,
  folders,
  hoveredFolderId,
  openFolderBoundary = null,
  properties = null,
  propertyStyles,
  fitKey,
  fitBounds = null,
  homeBounds = null,
  onHoverProject,
  onHoverFolder,
  onOpenFolder,
  interactive,
}: ListingMapAnnotationOptions): void {
  const projectsRef = useRef(lookupProjects ?? projects);
  projectsRef.current = lookupProjects ?? projects;
  const onViewportRef = useRef(onViewportChange);
  onViewportRef.current = onViewportChange;
  const onHoverRef = useRef(onHoverProject);
  onHoverRef.current = onHoverProject;
  const onHoverFolderRef = useRef(onHoverFolder);
  onHoverFolderRef.current = onHoverFolder;
  const onOpenFolderRef = useRef(onOpenFolder);
  onOpenFolderRef.current = onOpenFolder;
  // Read inside the fit effect, which must run on fitKey only.
  const fitBoundsRef = useRef(fitBounds);
  fitBoundsRef.current = fitBounds;

  // The overlays' current data. Kept in refs so one applier can (re)draw
  // everything whenever the style is able to take it: at start-up the style
  // can still report "not loaded" at the moment our effects run (sources
  // parsing in the worker, sprite or images pending), and a draw that gives
  // up then would leave the folders invisible until the next change.
  const highlightDataRef = useRef<FeatureCollection>(EMPTY);
  const foldersDataRef = useRef<FeatureCollection>(EMPTY);
  const propertiesDataRef = useRef<FeatureCollection>(EMPTY);
  const pendingApplyRef = useRef<(() => void) | null>(null);

  const applyAll = useCallback((map: mapboxgl.Map): boolean => {
    if (!ensureOverlays(map)) return false;
    setData(map, HIGHLIGHT_SOURCE, highlightDataRef.current);
    setData(map, FOLDERS_SOURCE, foldersDataRef.current);
    setData(map, PROPERTIES_SOURCE, propertiesDataRef.current);
    // Folder shapes beneath the projects; property outlines, then the
    // highlight and folder labels, on top.
    lowerBelowProjects(map, [FOLDERS_FILL, FOLDERS_LINE, FOLDERS_BOUNDARY_LINE, FOLDERS_OPEN_LINE]);
    raise(map, [PROPERTIES_LINE, HIGHLIGHT_FILL, HIGHLIGHT_LINE, FOLDERS_LABEL]);
    return true;
  }, []);

  /** Apply now, or keep trying on every idle until the style accepts it. */
  const requestApply = useCallback(
    (map: mapboxgl.Map) => {
      pendingApplyRef.current?.();
      pendingApplyRef.current = null;
      if (applyAll(map)) return;
      const handler = () => {
        if (applyAll(map)) {
          map.off("idle", handler);
          pendingApplyRef.current = null;
        }
      };
      map.on("idle", handler);
      pendingApplyRef.current = () => map.off("idle", handler);
    },
    [applyAll]
  );
  useEffect(() => () => pendingApplyRef.current?.(), []);

  // Highlight the hovered project's boundary.
  useEffect(() => {
    if (!map) return;
    const project = hoveredProjectId
      ? projectsRef.current.find((p) => String(p.id) === hoveredProjectId)
      : undefined;
    highlightDataRef.current = project?.boundary
      ? {
          type: "FeatureCollection",
          features: [
            {
              type: "Feature",
              properties: {},
              geometry: project.boundary as unknown as Geometry,
            },
          ],
        }
      : EMPTY;
    requestApply(map);
  }, [map, hoveredProjectId, projects, lookupProjects, requestApply]);

  // Every sub-folder's box or boundary, faint; the hovered one stronger; the
  // open folder's boundary around them all.
  useEffect(() => {
    if (!map) return;
    foldersDataRef.current = folderCollection(folders, hoveredFolderId, openFolderBoundary);
    requestApply(map);
  }, [map, folders, hoveredFolderId, openFolderBoundary, projects, requestApply]);

  // The drawn projects' property boundaries, each carrying its project's style.
  useEffect(() => {
    if (!map) return;
    propertiesDataRef.current = properties
      ? {
          type: "FeatureCollection",
          features: properties.features.map((f) => {
            const projectId = f.properties?.project_id;
            const style = resolveBoundaryStyle(
              projectId === undefined || projectId === null ? undefined : propertyStyles?.get(String(projectId)),
              PROPERTY_STYLE
            );
            return { ...f, properties: { ...f.properties, ...styleProps(style) } };
          }),
        }
      : EMPTY;
    requestApply(map);
  }, [map, properties, propertyStyles, requestApply]);

  // The map's own load, and any style swap (which drops every custom
  // source): put the overlays and their data back.
  useEffect(() => {
    if (!map) return;
    const handler = () => requestApply(map);
    map.on("load", handler);
    map.on("style.load", handler);
    return () => {
      map.off("load", handler);
      map.off("style.load", handler);
    };
  }, [map, requestApply]);

  // Frame the visible set when it changes. Never on hover: the whole point
  // of the dwell is that nothing jumps while the pointer wanders. With
  // nothing to frame, or projects on more than one continent (a fit would
  // be the whole globe), the home region is shown instead.
  const homeKey = homeBounds ? homeBounds.join(",") : "";
  useEffect(() => {
    if (!map || fitKey === undefined) return;
    const bounds = fitBoundsRef.current;
    const target = bounds && !isGlobeSpanning(bounds) ? bounds : homeBounds;
    if (!target) return;
    try {
      map.fitBounds([[target[0], target[1]], [target[2], target[3]]], {
        padding: target === homeBounds ? 20 : 40,
        duration: 500,
        maxZoom: MAX_AUTO_ZOOM,
        pitch: 0,
        bearing: 0,
      });
    } catch {
      // A pane too small for the padding: leave the camera where it is.
    }
    // homeKey stands in for the array's identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, fitKey, homeKey]);

  // "Show on map": centre the project and zoom in so it fills the pane. Only
  // ever on an explicit click; hovering never moves the camera.
  useEffect(() => {
    if (!map || !flyTo) return;
    const b = flyTo.bounds;
    try {
      map.fitBounds([[b[0], b[1]], [b[2], b[3]]], {
        padding: 60,
        duration: FLY_MS,
        maxZoom: MAX_AUTO_ZOOM,
        pitch: 0,
        bearing: 0,
        essential: true,
      });
    } catch {
      // A pane too small for the padding: leave the camera where it is.
    }
  }, [map, flyTo]);

  // Report the extent after every move, debounced so a drag reports once.
  useEffect(() => {
    if (!map) return;
    let timer: number | null = null;
    const report = () => {
      timer = null;
      const b = map.getBounds();
      if (!b) return;
      onViewportRef.current?.([b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]);
    };
    const onMoveEnd = () => {
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(report, 150);
    };
    map.on("moveend", onMoveEnd);
    if (map.loaded()) report();
    else map.once("load", report);
    return () => {
      map.off("moveend", onMoveEnd);
      map.off("load", report);
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [map]);

  // What the pointer is over: a project first, else a folder box. Own
  // listeners rather than Map.tsx's shared mousemove: that one is rebuilt on
  // every layers change and serves every page. Deduplicated so a stream of
  // mousemoves over one shape is reported once, or the dwell timer would
  // restart on every pixel.
  useEffect(() => {
    if (!map || !interactive) return;
    let lastProject: string | null = null;
    let lastFolder: number | null = null;
    const emitProject = (id: string | null) => {
      if (id === lastProject) return;
      lastProject = id;
      onHoverRef.current?.(id);
    };
    const emitFolder = (id: number | null) => {
      if (id === lastFolder) return;
      lastFolder = id;
      onHoverFolderRef.current?.(id);
    };
    const projectAt = (point: mapboxgl.PointLike): string | null => {
      // An id that is not on the style makes queryRenderedFeatures throw.
      const ids = projectsRef.current
        .map((p) => `project-${p.id}-fill`)
        .filter((id) => !!map.getLayer(id));
      if (ids.length === 0) return null;
      try {
        const raw = map.queryRenderedFeatures(point, { layers: ids })[0]?.properties?.id;
        return typeof raw === "string" && raw.startsWith("project-") ? raw.slice("project-".length) : null;
      } catch {
        return null;
      }
    };
    const folderAt = (point: mapboxgl.PointLike): number | null => {
      if (!map.getLayer(FOLDERS_FILL)) return null;
      try {
        const raw = map.queryRenderedFeatures(point, { layers: [FOLDERS_FILL] })[0]?.properties?.id;
        return typeof raw === "number" ? raw : null;
      } catch {
        return null;
      }
    };
    const onMove = (e: mapboxgl.MapMouseEvent) => {
      const projectId = projectAt(e.point);
      emitProject(projectId);
      const folderId = projectId === null ? folderAt(e.point) : null;
      emitFolder(folderId);
      // Map.tsx's own handler has already reset the cursor for this move.
      if (folderId !== null) map.getCanvas().style.cursor = "pointer";
    };
    const onOut = () => {
      emitProject(null);
      emitFolder(null);
    };
    const onClick = (e: mapboxgl.MapMouseEvent) => {
      if (projectAt(e.point) !== null) return; // the project click wins
      const folderId = folderAt(e.point);
      if (folderId !== null) onOpenFolderRef.current?.(folderId);
    };
    map.on("mousemove", onMove);
    map.on("mouseout", onOut);
    map.on("click", onClick);
    return () => {
      map.off("mousemove", onMove);
      map.off("mouseout", onOut);
      map.off("click", onClick);
      emitProject(null);
      emitFolder(null);
    };
  }, [map, interactive]);
}
